import React, { Activity, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import Alert from '@mui/material/Alert';

import { useDeepCompareEffect, useDeepCompareMemo, useMountedEffect } from '@clatter/platform';
import {
  FolderManagerAction,
  FolderManagerActionTypes,
  FolderManagerBreadcrumb,
  FolderManagerDragStartPayload,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerItemTypes,
  FolderManagerMimeTypes,
  FolderManagerViewTypes,
} from '../../interfaces';
import { FolderActionsConfig } from './Components/FolderManagerFoldersView/components/FolderRow';

import { useUpdateAssetMutation, useUpdateFolderMutation, useUpdatePresentationMutation } from '../../services';
import { ViewTypeSwitcher, GridZoomControls } from '../../shared';
import { getFolderManagerColumns, handleAssignItemsToFolder, markAlreadyAddedSlides } from '../../helpers';
import { NgBreadcrumbs, NgCard, NgCardHeader, NgCardContent, IconButtonRounded, StickyHeader } from '../UI';
import { DetailsRail } from './Components/DetailsRail';
import { FolderManagerHeader } from './Components/FolderManagerHeader';
import { FolderManagerGridView } from './Components/FolderManagerGridView';
import { FolderManagerFoldersView } from './Components/FolderManagerFoldersView/FolderManagerFoldersView';
import { FolderManagerTableView } from './Components/FolderManagerTableView';
import {
  FolderManagerSlidesViewFilters,
  SlidesViewFilters,
  DEFAULT_SLIDES_FILTERS,
} from './Components/FolderManagerSlidesViewFilters';
import FilterAltIcon from '@mui/icons-material/FilterAltOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import chevronIcon from '../../../assets/icons/chevron-light.svg';
import { AccessGuard } from '../AccessGuard/AccessGuard';
import { useSelector } from 'react-redux';
import { authSelectors } from '../../store';

//region INTERFACES

interface FolderManagerProps {
  isLoading: boolean;
  fileInputRef?: any;
  rootFolderId: number;
  noDataMessage?: string;
  selectedFolderId: any; // FIXME
  viewStateKey?: string;
  isPresentationThemesFolder?: boolean;
  disableSelectAll?: boolean;
  enableGroupByPptx?: boolean;

  hierarchyData: FolderManagerItem[];
  breadcrumbs: FolderManagerBreadcrumb[];
  disableUpload?: boolean;
  defaultViewType?: FolderManagerViewTypes;
  newButtonActionType?: string | null; //@todo - remove after cleanup
  minHeight?: string;
  acceptedDragTypes?: FolderManagerMimeTypes[]; // Array of accepted MIME types for dragging
  stickyHeaderTop?: number; // Top offset for sticky header in pixels
  availableViews?: (
    | FolderManagerViewTypes.SLIDES
    | FolderManagerViewTypes.GRID
    | FolderManagerViewTypes.LIST
    | FolderManagerViewTypes.FOLDERS
  )[];
  selectedKeys?: Set<string>;
  enableZoomControls?: boolean;
  isSlidesFolder?: boolean;
  showThemePicker?: boolean;
  slideRepositoryId?: number;
  filters?: SlidesViewFilters;
  onFiltersChange?: (filters: SlidesViewFilters) => void;
  noCard?: boolean;
  /** Enables compact grid layout (many small columns, tight gap) and compact item rendering */
  compactGrid?: boolean;
  compactGridMaxColumns?: number;
  hideMultiSelectBar?: boolean;
  hideDetailsChevron?: boolean;
  subHeaderContent?: React.ReactNode;
  breadcrumbMaxLength?: number;

  onResultCount?: (count: number) => void;
  onClearNewButtonAction: () => void;
  customMultiSelectActions?: (data: {
    selectedItems: any[];
    onClear: () => void;
    onAction?: FolderManagerAction;
  }) => void;
  onAction: (data: FolderManagerAction) => void;
  onSelect?: (data: { selectedRows: FolderManagerItem[]; clearSelection: () => void }) => void;
  onAddSlide?: (slide: any) => void; // Callback when adding a slide from expanded PPTX
  allowEntityClick?: boolean;
  disableActionColumn?: boolean;
  disableInternalDnD?: boolean;
  enableDnD?: boolean;
  controlledSelectedItems?: FolderManagerItem[];
  actionsConfig?: FolderActionsConfig;
}

//endregion

//region STYLED
const StyledMainContainer = styled.div<{ $detailsOpened?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  overflow: visible;

  @media (max-width: 860px) {
    flex-direction: column;
  }

  > div:first-child {
    flex: ${({ $detailsOpened }) => ($detailsOpened ? '0 0 70%' : '0 0 100%')};
    min-width: 0;
  }

  > div:nth-child(2) {
    flex: ${({ $detailsOpened }) => ($detailsOpened ? '1' : '0')};
  }
`;

const StyledZoomControlsWrapper = styled.div`
  align-self: flex-end;
`;

const StyledVerticalDivider = styled.div`
  width: 1px;
  height: 24px;
  background-color: ${({ theme }) => theme.palette.border};
`;

const StyledContent = styled.div`
  padding-top:  ${({ theme }) => theme.spacing.default};
`

const StyledSubHeaderContent = styled.div`
  padding: 6px 0;
`;

//endregion

const findItemRowIndex = ({ rows: rowItems, item }: { rows: FolderManagerItem[]; item: FolderManagerItem }) =>
  (rowItems || []).findIndex((row: FolderManagerItem) => {
    if ((item as any).instanceId && (row as any).instanceId) {
      return (item as any).instanceId === (row as any).instanceId;
    }
    return row?.id === item.id && row.type === item.type;
  });

export const FolderManager = ({
  isLoading,
  breadcrumbs,
  noDataMessage,
  rootFolderId,
  hierarchyData = [],
  defaultViewType = FolderManagerViewTypes.GRID,
  minHeight,
  onAction,
  onSelect,
  onAddSlide,
  allowEntityClick = false,
  disableActionColumn = false,
  disableInternalDnD = false,
  enableDnD = false,
  disableUpload = false,
  acceptedDragTypes,
  stickyHeaderTop,
  viewStateKey,
  isSlidesFolder,
  showThemePicker,
  slideRepositoryId,
  filters: filtersProp,
  onFiltersChange: onFiltersChangeProp,
  availableViews = [FolderManagerViewTypes.GRID, FolderManagerViewTypes.LIST],
  enableZoomControls = false,
  isPresentationThemesFolder,
  selectedKeys,
  controlledSelectedItems,
  noCard = false,
  disableSelectAll = false,
  enableGroupByPptx = false,
  actionsConfig,
  compactGrid,
  compactGridMaxColumns,
  hideMultiSelectBar,
  hideDetailsChevron,
  subHeaderContent,
  breadcrumbMaxLength,
  onResultCount,
}: FolderManagerProps) => {
  const location = useLocation();
  const { slug } = useParams();

  const [updateFolder] = useUpdateFolderMutation();
  const [updateAsset] = useUpdateAssetMutation();
  const [updatePresentation] = useUpdatePresentationMutation();

  //region STATE
  const [detailsOpened, setDetailsOpened] = useState(false);
  const [activeView, setActiveView] = useState(defaultViewType);
  const [selectedItems, setSelectedItems] = useState<FolderManagerItem[]>([]);
  const [notifications, setNotifications] = useState<{ type: string; message: string }[]>([]);
  const [zoomValue, setZoomValue] = useState(40);
  const [zoomExpanded, setZoomExpanded] = useState(true);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [internalSlidesFilters, setInternalSlidesFilters] = useState<SlidesViewFilters>(DEFAULT_SLIDES_FILTERS);
  const slidesFilters = filtersProp ?? internalSlidesFilters;
  const setSlidesFilters = onFiltersChangeProp ?? setInternalSlidesFilters;
  const isAdmin = useSelector(authSelectors.isAdmin);

  const topBarRef = useRef<HTMLDivElement | null>(null);
  const notificationContainer = document.getElementById('notification-container');

  const mappedRows = useMemo(() => {
    const rows = (hierarchyData || []).flatMap((item: FolderManagerItem, index: number) => {
      // in slide repo folders, flatten asset slides into individual items
      if (activeView !== FolderManagerViewTypes.FOLDERS && isSlidesFolder && item.slides?.length) {
        const actions = [
          { name: FolderManagerItemActionNames.SLIDE_DOWNLOAD, type: FolderManagerItemActionTypes.SINGLE },
        ];

        if (isAdmin) {
          actions.push({
            name: FolderManagerItemActionNames.PREVIEW_THEMES,
            type: FolderManagerItemActionTypes.SINGLE,
          });
        }

        return item.slides.map((slide: any, slideIndex: number) => ({
          ...slide,
          type: FolderManagerMimeTypes.SLIDE,
          id: `${(item as any)?.currentVersion?.ID}-${slideIndex}`,
          instanceId: `${(item as any)?.currentVersion?.ID}-${slideIndex}`,
          name: slide.displayName || `${item.name} - Slide ${slideIndex + 1}`,
          owner: (item as any).owner,
          createdBy: (item as any).createdBy,
          fileSize: (item as any).fileSize,
          createdAt: slide.itemData?.CreatedAt ?? (item as any).createdAt,
          updatedAt: slide.itemData?.UpdatedAt ?? (item as any).updatedAt,
          itemData: { ...item, slideData: slide.itemData },
          actions,
          rowIndex: index,
          status: 'completed',
        }));
      }

      return [
        {
          ...item,
          rowIndex: index,
          status: 'completed',
        },
      ];
    });

    return markAlreadyAddedSlides(rows, selectedKeys);
  }, [hierarchyData, isSlidesFolder, activeView, isPresentationThemesFolder, selectedKeys]);

  const columns = useDeepCompareMemo(() => {
    const columns = getFolderManagerColumns({ isLoading: isLoading, selectedItems: selectedItems, slug, onAction });

    if (disableActionColumn) {
      return columns.filter((c) => c.field !== 'action');
    }

    return columns;
  }, [disableActionColumn, selectedItems, isLoading]);

  const handleChangeView = useCallback((view: FolderManagerViewTypes) => {
    if (view === FolderManagerViewTypes.SLIDES) {
      setSelectedItems([]);
    }
    setActiveView(view);
  }, []);

  const handleCloseNotification = (index: number) => {
    setNotifications((prevNotifications) => {
      const updatedNotifications = [...prevNotifications];

      updatedNotifications.splice(index, 1);

      return updatedNotifications;
    });
  };

  const handleBreadcrumbClick = ({ item }: { item: any; index: number }) => {
    if (!slug) {
      onAction({ actionType: FolderManagerActionTypes.NAVIGATE, payload: item });
      return;
    }

    if (item?.slug) {
      onAction({ actionType: FolderManagerActionTypes.NAVIGATE, payload: item });
      return;
    }

    if (!item?.id) {
      console.log('ERROR:Folder Manager:: breadcrumb item id not provided');
      return;
    }

    onAction({ actionType: FolderManagerActionTypes.GET_SUB_FOLDERS, payload: { id: item.id } as FolderManagerItem });
  };

  const handleDragStart = (data: FolderManagerDragStartPayload) => {
    if (!data || !data?.id) {
      console.log('ERROR: invalid props');
      return;
    }

    if (!selectedItems || !selectedItems.length) {
      let selectedItem = data.item;

      if (!selectedItem) {
        console.error('Error, selected item data not found!');
        return;
      }

      // When dragging a slide, move its parent deck/presentation instead
      if (selectedItem.type === FolderManagerItemTypes.SLIDE && (selectedItem as any).parent) {
        selectedItem = (selectedItem as any).parent;
      }

      setSelectedItems([selectedItem as FolderManagerItem]);
    }
  };

  const resolveItemsToMove = (items: FolderManagerItem[]): FolderManagerItem[] => {
    const seen = new Set<number>();
    const resolved: FolderManagerItem[] = [];
    for (const item of items) {
      const target =
        item.type === FolderManagerItemTypes.SLIDE && (item as any).parent ? (item as any).parent : item;
      if (!seen.has(target.id)) {
        seen.add(target.id);
        resolved.push(target);
      }
    }
    return resolved;
  };

  const handleDragEnd = async (payload: IFolderManagerDragEndEvent) => {
    if (slug === 'trash') {
      console.log('Trash drag actions are disabled');
      return;
    }

    const itemsToMove = resolveItemsToMove(selectedItems);

    // drop onto root zone
    if ((payload.type as string) === 'root') {
      await handleAssignItemsToFolder({
        target: { id: rootFolderId, name: 'Root' },
        selectedItems: itemsToMove,
        updateFolderCb: updateFolder,
        updateAssetCb: updateAsset,
        updatePresentationCb: updatePresentation,
      });
      setSelectedItems([]);
      return;
    }

    // drop onto a folder
    if (payload.type === FolderManagerItemTypes.FOLDER) {
      const targetFolderData =
        mappedRows.find((item: any) => item.id === Number(payload.id) && item.type === FolderManagerItemTypes.FOLDER) ??
        payload.item;

      if (!targetFolderData) {
        console.error('Error moving items to folder. Target folder data not found!');
        return;
      }

      await handleAssignItemsToFolder({
        target: { id: targetFolderData.id, name: targetFolderData.name },
        selectedItems: itemsToMove,
        updateFolderCb: updateFolder,
        updateAssetCb: updateAsset,
        updatePresentationCb: updatePresentation,
      });

      setSelectedItems([]);
      return;
    }
  };

  const handleRowDoubleClick = ({ row }: { row: FolderManagerItem }) => {
    // handle folder click (allow navigation even in trash)
    if (row.type === FolderManagerMimeTypes.FOLDER) {
      setSelectedItems([]);
      onAction({ actionType: FolderManagerActionTypes.GET_SUB_FOLDERS, payload: row });
      return;
    }

    // Ignore trashed or deleted items for non-folder items
    if (row?.trashedAt || row?.deletedAt) {
      return;
    }

    if (row.type === FolderManagerMimeTypes.WORKSPACE) {
      setSelectedItems([]);
      onAction({ actionType: FolderManagerActionTypes.GET_WORKSPACE, payload: row });
      return;
    }

    if (row.type === FolderManagerMimeTypes.SLIDE_REPOSITORY) {
      setSelectedItems([]);
      onAction({ actionType: FolderManagerActionTypes.GET_SLIDE_REPOSITORY, payload: row });
      return;
    }

    // handle presentation double-click
    if (row.type === FolderManagerItemTypes.PRESENTATION) {
      setSelectedItems([]);
      onAction({ actionType: FolderManagerActionTypes.EDIT, payload: row });
      return;
    }

    // handle shortcut click
    if (row.sourceId) {
      if (!row?.source?.ID && !row?.source?.FolderID) {
        window.alert('Sorry original asset has been removed!');
        return;
      }

      setSelectedItems([]);
      onAction({
        actionType: FolderManagerActionTypes.GET_SUB_FOLDERS,
        payload: { id: row.source.FolderID } as FolderManagerItem,
      });
    }
  };

  const handleRowSelect = ({ event, item, visibleItems }: { event: any; item: any; visibleItems?: any[] }) => {
    event.preventDefault();

    if (allowEntityClick) {
      onAction({ actionType: FolderManagerActionTypes.ROW_CLICK, payload: { id: item.id } as FolderManagerItem });
      return;
    }

    const rows = visibleItems ?? mappedRows;

    setSelectedItems((prevSelectedItems) => {
      const selectedItem = prevSelectedItems[0]; // Access the currently selected item if it exists

      // Check for shift key and only one item selected
      if (event?.shiftKey && prevSelectedItems?.length === 1 && selectedItem?.id) {
        const startRowIndex = findItemRowIndex({ rows, item: selectedItem });
        const endRowIndex = findItemRowIndex({ rows, item: item });

        if (startRowIndex === -1) {
          console.log('ERROR: startRowIndex not found');
          return [];
        }

        const isExpanding = startRowIndex < endRowIndex;

        // Select the range of items between start and end indices
        return rows
          .slice(isExpanding ? startRowIndex : endRowIndex, isExpanding ? endRowIndex + 1 : startRowIndex + 1)
          .filter((row: any) => {
            const isFolder =
              row.type === FolderManagerMimeTypes.FOLDER || row.type === FolderManagerMimeTypes.WORKSPACE;
            const isTypeAccepted = !acceptedDragTypes || isFolder || acceptedDragTypes.includes(row.type);
            return isTypeAccepted && row.selectable !== false;
          });
      }
      // Clear selection if the clicked item is already selected
      else if (prevSelectedItems?.length === 1 && selectedItem.id === item.id && selectedItem.type === item.type) {
        return []; // Clear the selection
      }
      // Default case: select the clicked item
      else {
        return [item]; // Select the clicked item
      }
    });
  };

  const handleGroupHeaderSelect = useCallback((items: any[]) => {
    setSelectedItems((prev) => {
      const isAlreadySelected =
        items.length === 1 && prev.some((item) => item.id === items[0].id && item.type === items[0].type);
      return isAlreadySelected ? [] : items;
    });
  }, []);

  // Function to clear selection (exposed to parent)
  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);
  //endregion

  //region EFFECTS
  // toggle active view
  useMountedEffect(() => {
    setActiveView(defaultViewType);
  }, [defaultViewType]);

  // clear selection and close details rail when navigating to a different folder
  useEffect(() => {
    setSelectedItems([]);
    setDetailsOpened(false);
  }, [slug]);

  // Notify parent when selection changes
  useDeepCompareEffect(() => {
    if (onSelect) {
      onSelect({ selectedRows: selectedItems, clearSelection });
    }
  }, [selectedItems, clearSelection]);

  useEffect(() => {
    if (controlledSelectedItems !== undefined) {
      setSelectedItems(controlledSelectedItems);
    }
  }, [controlledSelectedItems]);

  useEffect(() => {
    if (!onResultCount || typeof onResultCount !== 'function') {
      return;
    }

    if (!isLoading) onResultCount?.(mappedRows.length);
  }, [mappedRows, isLoading]);

  const innerContent = (
    <>
      {availableViews.length > 1 && (
        <>
        <StickyHeader top={stickyHeaderTop} noWrap={!stickyHeaderTop}>
          <NgCardHeader
            ref={topBarRef}
            hideFilters={(hideMultiSelectBar || selectedItems.length <= 1) && !(enableZoomControls && zoomExpanded && activeView !== FolderManagerViewTypes.LIST)}
            noBorder={
              (hideMultiSelectBar || selectedItems.length <= 1) &&
              !(enableZoomControls && zoomExpanded && activeView !== FolderManagerViewTypes.LIST) &&
              !filtersExpanded &&
              !subHeaderContent
            }
            filters={
              <FolderManagerHeader
                filters={
                  <>
                    {enableZoomControls && activeView !== FolderManagerViewTypes.LIST && zoomExpanded && (
                      <StyledZoomControlsWrapper>
                        <GridZoomControls value={zoomValue} onChange={setZoomValue} />
                      </StyledZoomControlsWrapper>
                    )}
                  </>
                }
                onAction={onAction}
                selectedItems={hideMultiSelectBar ? [] : selectedItems}
                onClear={() => setSelectedItems([])}
                isLoading={isLoading}
              />
            }
          >
            <NgBreadcrumbs data={breadcrumbs} maxLength={breadcrumbMaxLength} onClick={handleBreadcrumbClick} />

            {(!availableViews || availableViews.length > 1) && (
              <ViewTypeSwitcher activeView={activeView} onChange={handleChangeView} availableViews={availableViews} />
            )}

            {(enableZoomControls || isSlidesFolder) && activeView !== FolderManagerViewTypes.LIST && (
              <StyledVerticalDivider />
            )}

            {enableZoomControls && activeView !== FolderManagerViewTypes.LIST && (
              <IconButtonRounded
                active={zoomExpanded}
                title={zoomExpanded ? 'Hide actions' : 'Show actions'}
                icon={<TuneIcon style={{ fontSize: '16px' }} />}
                materialIcon
                onClick={() => setZoomExpanded((prev) => !prev)}
              />
            )}

            {isSlidesFolder && activeView !== FolderManagerViewTypes.LIST && (
              <IconButtonRounded
                active={filtersExpanded}
                title={filtersExpanded ? 'Hide filters' : 'Show filters'}
                icon={<FilterAltIcon style={{ fontSize: '16px' }} />}
                onClick={() => setFiltersExpanded((prev) => !prev)}
                materialIcon
              />
            )}

            {!hideDetailsChevron && (
              <IconButtonRounded
                active={detailsOpened}
                title="Details"
                icon={<img src={chevronIcon} alt="Chevron" />}
                onClick={() => setDetailsOpened((prev) => !prev)}
                activeAccent
                flipIcon
              />
            )}
          </NgCardHeader>

          <Activity
            mode={
              isSlidesFolder && activeView !== FolderManagerViewTypes.LIST && filtersExpanded ? 'visible' : 'hidden'
            }
          >
            <FolderManagerSlidesViewFilters
              filters={slidesFilters}
              viewStateKey={viewStateKey}
              showThemePicker={showThemePicker}
              onChange={setSlidesFilters}
            />
          </Activity>
        </StickyHeader>

        {subHeaderContent && (
          <StyledSubHeaderContent>{subHeaderContent}</StyledSubHeaderContent>
        )}
        </>
      )}

      <StyledContent>
        {activeView === FolderManagerViewTypes.LIST ? (
          <FolderManagerTableView
            items={mappedRows}
            columns={columns}
            noDataMessage={noDataMessage}
            selectedRows={selectedItems}
            isLoading={isLoading}
            onAction={onAction}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onSelect={handleRowSelect}
            onDoubleClick={handleRowDoubleClick}
            storageKey={location.pathname.substring(1)}
            disableInternalDnD={disableInternalDnD}
            disableUpload={disableUpload}
            acceptedDragTypes={acceptedDragTypes}
          />
        ) : activeView === FolderManagerViewTypes.FOLDERS ? (
          <FolderManagerFoldersView
            items={mappedRows}
            selectedRows={selectedItems}
            isLoading={isLoading}
            noDataMessage={noDataMessage}
            minHeight={minHeight}
            viewStateKey={viewStateKey}
            acceptedDragTypes={acceptedDragTypes}
            disableUpload={disableUpload}
            isSlideRepositoryFolder={isSlidesFolder}
            enableDnD={enableDnD}
            rootFolderId={rootFolderId}
            selectedKeys={selectedKeys}
            disableSelectAll={disableSelectAll}
            enableGroupByPptx={enableGroupByPptx}
            filters={slidesFilters}
            showControls={showThemePicker}
            slideRepositoryId={slideRepositoryId}
            onFiltersChange={isSlidesFolder ? setSlidesFilters : undefined}
            actionsConfig={actionsConfig}
            onAction={onAction}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onSelect={handleRowSelect}
            onDoubleClick={handleRowDoubleClick}
          />
        ) : (
          <FolderManagerGridView
            items={mappedRows}
            selectedRows={selectedItems}
            noDataMessage={noDataMessage}
            isLoading={isLoading}
            minHeight={minHeight}
            viewStateKey={viewStateKey}
            onAction={onAction}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onSelect={handleRowSelect}
            onGroupHeaderSelect={isSlidesFolder ? handleGroupHeaderSelect : undefined}
            onDoubleClick={handleRowDoubleClick}
            disableInternalDnD={disableInternalDnD}
            disableUpload={disableUpload}
            acceptedDragTypes={acceptedDragTypes}
            slidesViewEnabled={isSlidesFolder}
            showThemePicker={showThemePicker}
            onAddSlide={onAddSlide}
            zoomValue={enableZoomControls ? zoomValue : undefined}
            compactGrid={compactGrid}
            compactGridMaxColumns={compactGridMaxColumns}
            selectedKeys={selectedKeys}
            filters={slidesFilters}
            onFiltersChange={isSlidesFolder ? setSlidesFilters : undefined}
          />
        )}
      </StyledContent>

      {notifications.length > 0 &&
        ReactDOM.createPortal(
          <>
            {notifications.map((notification, index) => (
              <Alert severity={notification.type as any} onClose={() => handleCloseNotification(index)}>
                {notification.message}
              </Alert>
            ))}
          </>,
          notificationContainer || document.body, // Use the specified container as the portal container
        )}
    </>
  );

  return (
    <StyledMainContainer $detailsOpened={detailsOpened}>
      {noCard ? (
        <div style={{ minHeight }}>{innerContent}</div>
      ) : (
        <NgCard>
          <NgCardContent minHeight={minHeight}>{innerContent}</NgCardContent>
        </NgCard>
      )}

      {detailsOpened && (
        <DetailsRail
          selectedItems={selectedItems}
          onClose={() => setDetailsOpened(false)}
          onAction={onAction}
          stickyTop={stickyHeaderTop}
          viewStateKey={viewStateKey}
        />
      )}
    </StyledMainContainer>
  );
};
